import { ClockCircleOutlined, EnvironmentOutlined, MoreOutlined } from "@ant-design/icons"
import { Button, Col, Dropdown, Flex, Row, Space } from "antd"
import { EXPERIENCE_STATES } from "constants/experiences"
import PropTypes from "prop-types"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { Tag } from "ui/Tag"
import { Paragraph, Text, Title } from "ui/Typography"
import { getActionConditions, getDropdownItems } from "../../utils/experience"
import styles from "./ExperienceCard.module.css"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import { getMenuConfig } from "./MenuConfig"
import ImagePlaceholder from "components/common/ImagePlaceholder"
import UserSvg from "assets/icons/ex-user.svg"
import PricingDetailsTooltip from "ui/PricingDetailsTooltip"
import { MODULES, ACTIONS } from "utils/permissions"
import { usePermissions } from "hooks/usePermissions"

export const ExperienceCard = ({ trip, onShare, onDuplicate, onDeactivate, onDelete, onPublish }) => {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()

  const { title, status, address, min_price, max_guests, publish_on_guides_site, publish_on_platform, available_hours_range, trip_prices } =
    trip || {}

  const price = min_price || 0
  const published = status === EXPERIENCE_STATES.published

  const menuConfig = useMemo(
    () =>
      getMenuConfig(t, {
        onShare,
        onDuplicate,
        onDeactivate,
        onDelete,
        onPublish
      }),
    [t, onShare, onDuplicate, onDeactivate, onDelete, onPublish]
  )

  const conditions = useMemo(() => getActionConditions(status), [status])
  const dropdownItems = useMemo(() => getDropdownItems(menuConfig, conditions), [menuConfig, conditions])
  const navigate = useNavigate()

  const handleEdit = () => {
    const isDraft = status === EXPERIENCE_STATES.draft
    if (isDraft) {
      navigate(routes.proNewExperienceV2Path({ id: trip?.id }))
    } else {
      const routeType = status === EXPERIENCE_STATES.deactivated ? "deactivated" : "published"
      const baseUrl = routes.proEditExperienceV2Path({ id: trip?.id })
      navigate(`${baseUrl}?route=${routeType}`)
    }
  }

  const handlePreviewClick = () => {
    const isDraft = status === EXPERIENCE_STATES.draft
    if (isDraft) {
      navigate(routes.proNewExperienceV2PreviewPath({ id: trip?.id }))
    } else {
      const routeType = status === EXPERIENCE_STATES.deactivated ? "deactivated" : "published"
      const baseUrl = routes.proEditExperienceV2PreviewPath({ id: trip?.id })
      navigate(`${baseUrl}&readOnlyPreview=true&route=${routeType}`)
    }
  }

  const isDeactivated = status === EXPERIENCE_STATES.deactivated

  let filteredDropdownItems = isDeactivated
    ? dropdownItems.filter((item) => item.key === "publish" || item.key === "delete" || item.key === "duplicate")
    : dropdownItems

  filteredDropdownItems = filteredDropdownItems.filter((item) => {
    switch (item.key) {
      case "deactivate":
        return hasPermission(MODULES.EXPERIENCES, ACTIONS.EDIT)
      case "publish":
        return trip?.can_edit
      case "delete":
        return trip?.can_edit
      case "duplicate":
        return hasPermission(MODULES.EXPERIENCES, ACTIONS.CREATE)
      case "share":
      default:
        return true
    }
  })

  const mainPhoto = useMemo(() => {
    const photos = trip?.photos || []
    return Array.isArray(photos) ? photos.find((p) => p.position === 0) : null
  }, [trip?.photos])

  return (
    <div className={styles.cardWrapper}>
      <div className={styles.imgWrapper}>
        {status && (
          <div className={`${styles.statusBadge} ${styles[status]}`}>
            {status === "published" && <Icon type="feather" name="Check" color="white" width={18} height={18} />}
            {status === "unpublished" ? "Deactivated" : status}
          </div>
        )}
        {mainPhoto ? (
          <>
            {/* Blurred background layer */}
            <img
              src={mainPhoto.preview_url}
              alt=""
              className={styles.blurredBackground}
            />
            {/* Main media content */}
            <div className={styles.mediaContent}>
              <img src={mainPhoto.preview_url} alt={title} className={styles.img} />
            </div>
          </>
        ) : (
          <ImagePlaceholder className={styles.img} label={false} />
        )}
      </div>
      <div className={styles.content}>
        <div>
          <Row justify="space-between" align="top">
            <Col>
              <Title level={5} style={{ margin: 0, fontWeight: 500 }} className="pb-xs">
                {title}
              </Title>
            </Col>
            <Col>
              <Flex gap={8} align="center">
                <Text className="color-text-secondary">from</Text>
                <Text className="text-color-primary" strong>
                  ${Number.parseFloat(price).toFixed(2)}
                </Text>
                <PricingDetailsTooltip tripPrices={trip_prices} />
              </Flex>
            </Col>
          </Row>
          <Row justify="space-between" align="top">
            {address && (
              <Col xxl={14} xl={14} span={24} className="locationText">
                <Space align="top">
                  <EnvironmentOutlined className="color-text-secondary-dark" />
                  <Paragraph ellipsis={{ rows: 2 }} className="mb-0 color-text-secondary-dark">
                    {address}
                  </Paragraph>
                </Space>
              </Col>
            )}
            <Col>
              <Space size={16}>
                <Space>
                  <img src={UserSvg} alt="" />
                  <Text className="lowercased color-text-secondary-dark">
                    {max_guests} {t("global.max")}
                  </Text>
                </Space>
                <Space>
                  <ClockCircleOutlined />
                  <Text className="color-text-secondary-dark">{`${available_hours_range ?? "--"}`}</Text>
                </Space>
              </Space>
            </Col>
          </Row>
          {published && (
            <Row className="mt-xs">
              <Col span={24}>
                <Flex gap={5} align="center" wrap="wrap" className={styles.tagCardWraper}>
                  <Text variant="caption" className={`mr-xs ${styles.status}`}>
                    {t("experiences.empty_state.published")}
                  </Text>
                  {publish_on_platform && (
                    <Tag type="success">
                      <Flex gap={4} align="center">
                        <Icon type="feather" name="Check" color="currentColor" width={16} height={16} />
                        {t("global.ds_marketplace")}
                      </Flex>
                    </Tag>
                  )}
                  {publish_on_guides_site && (
                    <Tag type="success">
                      <Flex gap={4} align="center">
                        <Icon type="feather" name="Check" color="currentColor" width={16} height={16} />
                        {t("global.guides_website")}
                      </Flex>
                    </Tag>
                  )}
                </Flex>
              </Col>
            </Row>
          )}
        </div>
        <Row justify="space-between" align="middle" className={styles.publishCard}>
          <Col>
            <Space>
              {trip?.can_edit && (
                <Button color="primary" variant="outlined" onClick={handleEdit}>
                  {status === EXPERIENCE_STATES.draft ? t("global.continue_creating") : t("global.edit")}
                </Button>
              )}
              {status !== EXPERIENCE_STATES.draft && (
                <Button color="primary" variant="outlined" onClick={handlePreviewClick}>
                  {t("global.preview")}
                </Button>
              )}
            </Space>
          </Col>
          <Col>
            <Dropdown
              menu={{
                items: filteredDropdownItems,
                onClick: (info) => {
                  const item = filteredDropdownItems?.find((i) => i.key === info.key)
                  if (item && typeof item.onClick === "function") {
                    item.onClick()
                  }
                }
              }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <div className={styles.actionButtonWrapper}>
                <Button color="primary" variant="text" icon={<MoreOutlined />} />
              </div>
            </Dropdown>
          </Col>
        </Row>
      </div>
    </div>
  )
}
ExperienceCard.propTypes = {
  trip: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.oneOf(Object.values(EXPERIENCE_STATES)).isRequired,
    min_price: PropTypes.number,
    address: PropTypes.string,
    max_guests: PropTypes.number.isRequired,
    trip_length: PropTypes.oneOf(["full_day", "half_day"]),
    available_hours: PropTypes.arrayOf(
      PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string
      })
    ),
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        preview_url: PropTypes.string.isRequired
      })
    ),
    cropped_photos: PropTypes.arrayOf(
      PropTypes.shape({
        preview_url: PropTypes.string.isRequired
      })
    ),
    publish_on_guides_site: PropTypes.bool,
    publish_on_platform: PropTypes.bool,
    available_hours_range: PropTypes?.string,
    trip_prices: PropTypes.arrayOf(
      PropTypes.shape({
        price: PropTypes.number,
        guests: PropTypes.number,
        pricing_type: PropTypes.string,
        included_guests: PropTypes.number,
        guests_included_in_flat_rate: PropTypes.number,
        price_per_person: PropTypes.number,
        price_per_child: PropTypes.number,
        price_per_infant: PropTypes.number,
        price_per_passenger: PropTypes.number
      })
    ),
    can_edit: PropTypes.bool
  }).isRequired,
  uiType: PropTypes.oneOf(["draft", "published", "deactivated", "unpublished"]),
  onShare: PropTypes.func,
  onDuplicate: PropTypes.func,
  onDeactivate: PropTypes.func,
  onDelete: PropTypes.func,
  onPublish: PropTypes.func
}

ExperienceCard.defaultProps = {
  uiType: "published",
  onShare: () => {},
  onDuplicate: () => {},
  onDeactivate: () => {},
  onDelete: () => {},
  onPublish: () => {}
}
